import type { Messages } from "@lingui/core";

// Full BCP 47 locale tags, matching ui-hub's convention: English needs a
// region to disambiguate en-GB (source) from en-US, so every other
// supported market is tagged the same way for consistency.
export enum Locale {
  EN_GB = "en-GB",
  EN_US = "en-US",
  FR_FR = "fr-FR",
  DE_DE = "de-DE",
  ES_ES = "es-ES",
  IT_IT = "it-IT",
  NL_NL = "nl-NL",
  PT_PT = "pt-PT",
}

export const DEFAULT_LOCALE = Locale.EN_GB;

// Compiled Lingui catalogs, one dynamic import per locale so only the
// active locale's messages are fetched at runtime (not bundled by default).
const catalogImports: Record<Locale, () => Promise<{ messages: Messages }>> = {
  // .mjs, not .js: `lingui compile` defaults to a CommonJS
  // (`module.exports = ...`) bundle, which native ESM `import()` can't
  // consume here (this package, and its shipped output, are ESM) --
  // `--namespace es` (see the i18n:compile script) makes it emit real
  // `export` syntax, and emits it into a `.mjs` file to signal that.
  [Locale.EN_GB]: () => import("../catalogs/en-GB/messages.mjs"),
  [Locale.EN_US]: () => import("../catalogs/en-US/messages.mjs"),
  [Locale.FR_FR]: () => import("../catalogs/fr-FR/messages.mjs"),
  [Locale.DE_DE]: () => import("../catalogs/de-DE/messages.mjs"),
  [Locale.ES_ES]: () => import("../catalogs/es-ES/messages.mjs"),
  [Locale.IT_IT]: () => import("../catalogs/it-IT/messages.mjs"),
  [Locale.NL_NL]: () => import("../catalogs/nl-NL/messages.mjs"),
  [Locale.PT_PT]: () => import("../catalogs/pt-PT/messages.mjs"),
};

export const getCatalog = async (locale: Locale) => {
  const catalogImport =
    catalogImports[locale] ?? catalogImports[DEFAULT_LOCALE];
  const catalogModule = await catalogImport();
  return catalogModule.messages;
};
